import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertBar,
	Form,
	FormattedText,
	ToolbarAction,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useUserGroupPermissionExplanationDialog } from "./UserGroupPermissionExplanationDialog";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import {
	getViewModel,
	save,
	UserGroupPermission,
	UserGroupPermissionsModel
} from "client/Api/UserGroupsManagement/Permissions/userGroupPermissions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { IFormHandler, useFormHandler } from "client/Forms";
import { UserGroupPermissionsSection } from "./UserGroupPermissionsSection";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import {
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import styles from "./PermissionManagementPage.module.less";

export const PermissionManagementPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserGroupPermissionsModel>(
		(): UserGroupPermissionsModel => ({
			canCreateApps: UserGroupPermission.NotSet,
			canCreateSharedGroups: UserGroupPermission.NotSet,
			canCreateAnnouncementsAndCategories: UserGroupPermission.NotSet,
			canEditOwnWorkspace: UserGroupPermission.Allowed,
			canMakeUserRequests: UserGroupPermission.NotSet,
			canCreateKnowledgeItemsAndCategories: UserGroupPermission.NotSet,
			canCreateEventsAndCategories: UserGroupPermission.NotSet,
			canCreateHubTemplates: UserGroupPermission.NotSet,
			canUploadAndManageAppIcons: UserGroupPermission.NotSet,
			canCreateAndManageSpaces: UserGroupPermission.NotSet,
			canManageAllApps: UserGroupPermission.NotSet,
			canManageBillingModulesAndLicenses: UserGroupPermission.NotSet,
			canManageSharedTileGroups: UserGroupPermission.NotSet,
			canManageUsersGroupsAndUserProvisioning: UserGroupPermission.NotSet,
			canSendDirectPopups: UserGroupPermission.Allowed,
			canCreateAndManageCommunitiesAndCommunityPosts: UserGroupPermission.NotSet,
			canManageBranding: UserGroupPermission.NotSet,
			canCreateAndManageIntegrationBuilderIntegrations: UserGroupPermission.NotSet,
			canViewAnalytics: UserGroupPermission.NotSet
		})
	);
	const [lastSavedFormData, setLastSavedFormData] = useState<UserGroupPermissionsModel | undefined>();
	const [isInfoDismissed, setIsInfoDismissed] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const formHandler: IFormHandler<UserGroupPermissionsModel> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	useActiveSettingsMenuItem(SettingsPageKind.GroupPermissions);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const { permissions } = await getViewModel(signal)
						.catch(handlePageDataLoadingError);

					setFormData(permissions);
					setLastSavedFormData(permissions);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const [
		userGroupPermissionExplanationDialog,
		openUserGroupPermissionExplanationDialog
	] = useUserGroupPermissionExplanationDialog();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy || !lastSavedFormData) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<UserGroupPermissionsModel> = formHandler.validation;

			const validationResult: InputValidationResult = await validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			let wasChangedToDeny: boolean = false;

			for (const permissionName of Object.keys(lastSavedFormData)) {
				wasChangedToDeny = formData[permissionName as keyof UserGroupPermissionsModel] === UserGroupPermission.Denied
					&& lastSavedFormData[permissionName as keyof UserGroupPermissionsModel] !== UserGroupPermission.Denied;

				if (wasChangedToDeny) {
					break;
				}
			}

			if (wasChangedToDeny) {
				const result: TryResult<void> = await openConfirmationDialog({
					title: (
						<FormattedText
							text={localize("_AreYouSureYouWantToSetTheDefaultStateToDeny")}
							thingsToReplace={[
								{ what: "{{Deny}}", filler: localize("_Deny") }
							]}
						/>
					),
					children: (
						<FormattedText
							text={localize("_EveryUserWillBeRestrictedFromDoingTheTaskEvenIfTheyBelongToAGroupThatHasThePermissionSetToAllow")}
							thingsToReplace={[
								{ what: "{{Allow}}", filler: localize("_Allow") }
							]}
						/>
					),
					confirmText: localize("_Yes"),
					cancelText: localize("_No"),
					trackingId: "deny-default-state-confirmation-dialog"
				});

				if (!result.wasSuccessful) {
					setIsBusy(false);
					return;
				}
			}

			try {
				await save(
					formData,
					globalCancellation
				);

				alertManager.showSuccess(localize("_ChangesSaved"));
				setLastSavedFormData(formData);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			lastSavedFormData,
			openConfirmationDialog
		]
	);

	const defaultActions = useMemo(
		(): readonly EditPermissionAction[] => {
			return listDefaultActions({
				done: submitHandler,
				cancel: (model) => setFormData(model),
			});
		},
		[submitHandler]
	);

	const topBarActions = useMemo(
		(): readonly EditPermissionAction[] => {
			return buildActionsForTopBar<EditPermissionActionContext, EditPermissionAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): EditPermissionActionContext => ({
			lastSavedFormData
		}),
		[lastSavedFormData]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_DefaultPermissions")}
			onMoreInfo={openUserGroupPermissionExplanationDialog}
		>
			{!isInfoDismissed && (
				<div className={styles.alert}>
					<AlertBar
						kind="info"
						isInline
						isDismissable
						onDismiss={() => setIsInfoDismissed(true)}
						trackingId="default-permissions-info-alert"
					>
						{localize("_DefaultGroupPermissionsExplanation")}
					</AlertBar>
				</div>
			)}

			<Form onSubmit={submitHandler}>
				<SettingsPage.Section height="stretch">
					<UserGroupPermissionsSection
						attachable={formHandler}
					/>
				</SettingsPage.Section>
			</Form>

			<AiAssistantFabSpacing />

			{userGroupPermissionExplanationDialog}
			{confirmationDialog}
		</SettingsPage>
	);
};
PermissionManagementPage.displayName = "PermissionManagementPage";

type EditPermissionActionContext = { lastSavedFormData: UserGroupPermissionsModel | undefined; };

type EditPermissionAction = ToolbarAction<EditPermissionActionContext>;

type DefaultActionsHandlers = {
	done: () => void;
	cancel: (model: UserGroupPermissionsModel) => void;
};

function listDefaultActions(handlers: DefaultActionsHandlers): readonly EditPermissionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			icon: "floppy-disk",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.done(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			icon: "xmark",
			title: localize("_Cancel"),
			canExecute: (x) => isNotNil(x.lastSavedFormData),
			execute: (x) => handlers.cancel(
				ensureNotNil(x.lastSavedFormData)
			)
		}
	];
}

export const userGroupPermissionRouting = {
	overview: "/UserGroupPermissions"
};